import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import CustomPopup from "../../../../components/CustomPopup/CustomPopup";
import CloseIcon from "@mui/icons-material/Close";
import "./ReKycPopup.scss";
import CommonSuccessPopupBody from "../../../../components/CustomSuccessPopupBody/CommonSuccessPopupBody";
import {
  InAppFeatureFuncCodes,
  LiBaseCaseCreateReqBody,
  LiDestinationLobId,
  LiLobCode,
  pageMessages,
} from "../../../../utils/constants";
import CommonFeedbackPopupBody from "../../../../components/CustomFeedbackPopupBody/CommonFeedbackPopupBody";
import ArrowRightIcon from "../../../../assets/icons/red-arrow-right.svg";
import { Colors } from "../../../../styles/constant";
import bulbIcon from "../../../../assets/icons/famicons_bulb-sharp.svg";
import usePageMessages from "../../../../hooks/useCmsPageMessages";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../redux-store/CustomReduxHooks";
import {
  buildSSOPayload,
  checkNPSSessionStorage,
  createOneServiceSubRequestBody,
  getActivePolicy,
  getValidCiiNumber,
} from "../../../../utils/helper";
import { T_LiProfileDetails } from "../../LifeInsurance_Types";
import {
  useLazyFetchLiProfileDetailsQuery,
  useLazyLiCrmCaseCreateQuery,
} from "../../LifeInsurance_Api";
import { API_STATUS_CODE } from "../../../../utils/ApiEndpoints";
import { setError } from "../../../../redux-store/reducers/CsErrorSlice";
import { setIsReKycCaseCreateCalled } from "../../LifeInsurance_Slice";
import { triggerGenerateSSOOld } from "../../../Feature_SSO/Sso_Slice";
import { TriggerGenerateSSO_OLD } from "../../../Feature_SSO/Sso_Types";
import { useNavigate } from "react-router-dom";
import CustomCircularLoader from "../../../../components/CustomCircularLoader/CustomCircularLoader";
import { isEmpty } from "lodash";
import { T_CustomerLobDetailObj } from "../../../Feature_Login/Login_Types";

interface Props {
  open: boolean;
  handleClose: any;
  isFromQuickActionMenu?: boolean;
}

const ReKycPopup: FC<Props> = ({
  open,
  handleClose,
  isFromQuickActionMenu = false,
}) => {
  const [isReKycComplete, setIsReKycComplete] = useState<boolean>(false);
  const [npsRatingReq, setNpsRatingReq] = useState<boolean>(true);
  const [commonFeedbackPopupOpen, setCommonFeedbackPopupOpen] =
    useState<boolean>(false);
  const [isBtnLoading, setIsBtnLoading] = useState<string>("");
  const [liProfileDetailsApi] = useLazyFetchLiProfileDetailsQuery();
  const [liCrmCaseCreateApi] = useLazyLiCrmCaseCreateQuery();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const masterData = useAppSelector(
    (state) => state.GeneralAppElementsSlice?.masterData
  );
  const menuData = useAppSelector(
    (state) => state.GeneralAppElementsSlice.quickActionMenu
  );
  const reKycData = menuData?.["02"]?.[1]?.menuDetails?.find(
    (i) => i.menuName?.toLowerCase() === "rekyc status"
  );

  const ssoSlice = useAppSelector(
    (state) => state.SsoSlice.ssoData as TriggerGenerateSSO_OLD
  );

  const { dashboardDetails, policyDetails, reKyc } = useAppSelector(
    (state) => state.LifeInsuranceSlice
  );

  const updateAddressItem = masterData?.masterDetail?.["update-address"];
  const updateAddressFunctionalityCode =
    updateAddressItem?.[0]?.masterKey || "ADDRU";
  const updateAddressRedirectionUrl = updateAddressItem?.[0]?.masterValue || "";
  const reKycPopupContent = usePageMessages(
    pageMessages["li-re-kyc-confirmation"]
  );
  const updateAddressPopupContent = usePageMessages(
    pageMessages["li-re-kyc-update-address"]
  );
  const reKycEligibilityNote = usePageMessages(
    pageMessages["li-re-kyc-eligibility-note"]
  );

  const reKycMandatoryNote = usePageMessages(
    pageMessages["li-re-kyc-mandatory-note"]
  );

  const activePolicy = useMemo(() => {
    return getActivePolicy(dashboardDetails, policyDetails);
  }, [dashboardDetails, policyDetails]);

  const csAppSlice = useAppSelector((state) => state.CsAppSlice);
  const customerAndLobDetails =
    csAppSlice.userDetails?.customerAndLobDetails || [];

  const ciiNo = getValidCiiNumber(customerAndLobDetails, LiLobCode);

  useEffect(() => {
    if (
      (isBtnLoading === "Modify" &&
        (!ssoSlice || isEmpty(ssoSlice) || !ssoSlice?.showLoading)) ||
      (isBtnLoading === "updateAddress" &&
        (!ssoSlice || isEmpty(ssoSlice) || !ssoSlice?.showLoading))
    ) {
      setIsBtnLoading("");
    }
  }, [ssoSlice]);

  const redirectToLob_Li = useCallback(
    (
      label: string,
      functionalityCode: string,
      destinationLobId: string,
      redirectionUrl: string
    ) => {
      const panNo = csAppSlice.userDetails?.pan;
      const name = csAppSlice.userDetails?.name;
      const dob = csAppSlice.userDetails?.dob;

      setIsBtnLoading(label);
      dispatch(
        triggerGenerateSSOOld(
          buildSSOPayload(
            csAppSlice,
            ssoSlice,
            redirectionUrl,
            functionalityCode,
            destinationLobId,
            ciiNo,
            panNo,
            name,
            dob,
            ssoSlice.lobClientId
          ) as TriggerGenerateSSO_OLD
        )
      );
    },
    []
  );

  const handleCrmCaseCreate = async (
    profileDetailsResponse: T_LiProfileDetails[]
  ) => {
    if (isEmpty(profileDetailsResponse)) {
      return;
    }
    const activeProfileDetails =
      profileDetailsResponse.find(
        (item) => item.polId === activePolicy?.PolicyDetails?.POL_ID
      ) || profileDetailsResponse[0];

    let reqBody: any = { ...LiBaseCaseCreateReqBody };
    reqBody.CODE = "PNFT032";
    reqBody.CUSTOMERID = activePolicy?.PolicyDetails?.CLIENT_ID || "";
    reqBody.POLICYNO = activePolicy?.PolicyDetails?.POL_ID || "";
    reqBody.FunctionalityCode = InAppFeatureFuncCodes.LI_RE_KYC;
    reqBody.Address1 = activeProfileDetails?.currAddLine1;
    reqBody.Address2 = activeProfileDetails?.currAddLine2;
    reqBody.Address3 = activeProfileDetails?.currAddLine3;
    reqBody.Area = activeProfileDetails?.currAddArea;
    reqBody.City = activeProfileDetails?.cliCity;
    reqBody.Pincode = activeProfileDetails?.cliPostalCode;
    reqBody.State = activeProfileDetails?.cliState;
    reqBody.Country = activeProfileDetails?.cliCountry;
    reqBody.Address_Valid_status = "Y";
    reqBody.Address_STP_Flag = "Y";

    const filteredOneServiceSubRequestBody = createOneServiceSubRequestBody(
      masterData,
      LiLobCode,
      customerAndLobDetails[0]
    );

    const crmCaseCreateApiPayload = {
      request: {
        CreateCaseRequest: reqBody,
      },
      oneServiceSubRequest: filteredOneServiceSubRequestBody,
    };
    return await liCrmCaseCreateApi(crmCaseCreateApiPayload).then((res) => {
      setIsBtnLoading("");
      if (res?.isError || res.data?.code !== API_STATUS_CODE.SUCCESS) {
        dispatch(
          setError({
            error: new Error("Error"),
            message: "case create API failed",
          })
        );
        return false;
      }
      if (res.data?.code === API_STATUS_CODE.SUCCESS) {
        const NPSReq = checkNPSSessionStorage(
          "Li-Re-Kyc",
          "LifeInsuranceReKyc"
        );
        setNpsRatingReq(NPSReq);
        dispatch(setIsReKycCaseCreateCalled(true));
        setIsReKycComplete(true);
        return;
      }
    });
  };

  const handleModifyClick = () => {
    redirectToLob_Li(
      "Modify",
      updateAddressFunctionalityCode,
      LiDestinationLobId,
      reKycData?.redirectionalUrl || updateAddressRedirectionUrl
    );
  };

  const handleRateYourExperience = async () => {
    setCommonFeedbackPopupOpen(true);
  };

  const handleConfirmClick = async () => {
    setIsBtnLoading("Confirm");
    const primaryUserLiLobId = customerAndLobDetails?.filter(
      (item: T_CustomerLobDetailObj) =>
        item.LOB === LiLobCode && item.PRIMARY_SECONDARY_USER === "primary"
    );
    await liProfileDetailsApi({ clientId: [primaryUserLiLobId[0].LOBID] }).then(
      (res) => {
        if (res?.isError || res.data?.code !== API_STATUS_CODE.SUCCESS) {
          setIsBtnLoading("");
          dispatch(
            setError({
              error: new Error("Error"),
              message: "case create API failed",
            })
          );
          return false;
        }
        if (res.data?.code === API_STATUS_CODE.SUCCESS) {
          handleCrmCaseCreate(
            res.data.payload?.fetchProfileDetailsAttributesList || []
          );
        }
      }
    );
  };

  const handleUpdateAddressClick = () => {
    redirectToLob_Li(
      "updateAddress",
      updateAddressFunctionalityCode,
      LiDestinationLobId,
      updateAddressRedirectionUrl
    );
  };

  const renderFeedBackPopup = () => {
    return (
      <CustomPopup open={open} handleOpen={() => {}} className="feedback-popup">
        <Box className="feedback-popup-content">
          <CommonFeedbackPopupBody
            linkOnClick={handleClose}
            module={"ReKYC"}
            mode={"LifeInsuranceReKyc"}
            value={"Li-Re-Kyc"}
            selectedPolicyId={activePolicy?.PolicyDetails?.POL_ID}
          />
        </Box>
      </CustomPopup>
    );
  };

  let isAddressAvailable = !isEmpty(
    activePolicy?.PolicyDetails?.CURRENT_ADDRESS
  );

  return (
    <>
      {commonFeedbackPopupOpen ? (
        renderFeedBackPopup()
      ) : (
        <CustomPopup
          open={open}
          handleOpen={
            window.location.pathname === "/home" || isFromQuickActionMenu
              ? handleClose
              : () => navigate("/home")
          }
          btnLabel={
            isReKycComplete
              ? isFromQuickActionMenu
                ? "Skip to Details Page"
                : "Skip to Home Page"
              : "Close"
          }
          className="life-insurance-rekyc-popup"
          btnStartIcon={
            !isReKycComplete && <CloseIcon sx={{ fontSize: "1rem" }} />
          }
          btnEndIcon={
            isReKycComplete && (
              <img
                src={ArrowRightIcon}
                alt="right-arrow"
                style={{ color: Colors.red }}
              />
            )
          }
        >
          <Box>
            {isReKycComplete ? (
              <CommonSuccessPopupBody
                title="Congratulations!"
                des="You have successfully confirmed your details for completion of Re-KYC."
                btnLabel={
                  npsRatingReq ? "Rate Your Experience" : "Skip to Home Page"
                }
                btnOnClick={
                  npsRatingReq ? handleRateYourExperience : handleClose
                }
                isChangesForLifeInsurance={true}
                cmsKey={pageMessages["li-re-kyc-success"]}
              />
            ) : (
              <Box className="re-kyc-confirmation-content-wrapper">
                <Typography textAlign="center" variant="h3" lineHeight="120%">
                  {isAddressAvailable
                    ? reKycPopupContent?.attributes?.title
                    : updateAddressPopupContent?.attributes?.title}
                </Typography>
                <Typography
                  textAlign="center"
                  variant="subtitle2"
                  marginTop=".625rem"
                >
                  <b>{activePolicy?.PolicyDetails?.OWNER_NAME}</b>
                  {isAddressAvailable
                    ? reKycPopupContent?.attributes?.message
                    : updateAddressPopupContent?.attributes.message}
                </Typography>
                {isAddressAvailable && (
                  <Box className="address-box">
                    <Typography textAlign="left" variant="subtitle2">
                      <b>Address:</b>{" "}
                      {activePolicy?.PolicyDetails?.CURRENT_ADDRESS}
                    </Typography>
                  </Box>
                )}
                <Box className="note-wrapper">
                  <img src={bulbIcon} alt="bulb_icon" />
                  <Typography
                    color="#1F874C"
                    fontWeight={400}
                    marginLeft=".375rem"
                    fontSize={12}
                    lineHeight="120%"
                  >
                    <b> Note:</b>{" "}
                    {reKyc.mandatory
                      ? reKycMandatoryNote?.attributes?.message
                      : reKycEligibilityNote?.attributes?.message}
                  </Typography>
                </Box>
                <Box className="buttons-wrapper">
                  {isAddressAvailable ? (
                    <>
                      <Button
                        fullWidth
                        variant="contained"
                        className={`modify-btn`}
                        onClick={handleModifyClick}
                      >
                        {isBtnLoading === "Modify" ? (
                          <CustomCircularLoader size={20} />
                        ) : (
                          "Modify"
                        )}
                      </Button>
                      <Button
                        fullWidth
                        variant="contained"
                        className={`confirm-btn`}
                        onClick={handleConfirmClick}
                      >
                        {isBtnLoading === "Confirm" ? (
                          <CustomCircularLoader size={20} color="white" />
                        ) : (
                          "Confirm"
                        )}
                      </Button>
                    </>
                  ) : (
                    <Button
                      fullWidth
                      variant="contained"
                      className={`update-address-btn`}
                      onClick={handleUpdateAddressClick}
                    >
                      {isBtnLoading === "updateAddress" ? (
                        <CustomCircularLoader size={20} color="white" />
                      ) : (
                        "Update Address"
                      )}
                    </Button>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        </CustomPopup>
      )}
    </>
  );
};

export default ReKycPopup;
